/**
 * Modo tenant (`PLAN-DOMINIOS-VENDEDOR.md` §0.1, E1 puntos 1 y 6): cuando un subdominio de
 * tienda (`adidas.jopit.com.ar`) resuelve, un Worker en el borde proxea el HTML del
 * marketplace e inyecta `window.__JOPIT_TENANT__` antes de que este script corra:
 *
 *   window.__JOPIT_TENANT__ = {
 *     shopId: "6932e771ddbd0dfb14ae1805",
 *     slug: "adidas",
 *     host: "adidas-staging.jopit.com.ar",
 *     mode: "subdomain",
 *     name: "Adidas",
 *     description: "",
 *     bannerImage: "https://firebasestorage.googleapis.com/...",
 *   }
 *
 * Es un global inyectado en el HTML — no confiable por construcción. Puede no existir (el
 * 100% del tráfico de hoy, `marketplace.jopit.com.ar`) o venir mal formado. `getValidTenant()`
 * es el único lugar que decide si hay "tenant válido": sin `shopId` no hay nada que rutear,
 * así que cualquier otra forma cae a `null` — el comportamiento normal del marketplace.
 *
 * OJO: esto NO es una validación de seguridad. Es un global que cualquiera puede pisar desde
 * la consola; sirve para elegir qué layout de rutas montar y qué le llega a los MFEs por
 * `customProps.tenant` (pintado, no autorización).
 */
export function getValidTenant(source = typeof window !== "undefined" ? window.__JOPIT_TENANT__ : undefined) {
    if (source === null || source === undefined || typeof source !== "object") return null;
    if (typeof source.shopId !== "string" || source.shopId.trim() === "") return null;
    return source;
}

/*
 * Rutas que son de JOPIT, no de la tienda, y por lo tanto no tienen nada que hacer bajo el
 * subdominio de un vendedor.
 *
 * Hasta ahora el layout de tenant las montaba igual, con el chrome del marketplace adentro, como
 * red para URLs viejas. Pero `<tienda>.jopit.com.ar/user/profile` es la cuenta de Jopit servida
 * bajo la marca de otro, que es exactamente lo que la iniciativa vino a sacar. Y la red no
 * protegía a nadie: hasta que el layout de tenant salió a producción ese host servía el home del
 * marketplace, así que no existen bookmarks viejos que rescatar.
 *
 * Se comparan por PREFIJO de segmento, no por igualdad: `/orders` tiene que llevarse `/orders/123`
 * y `/user/profile` sus subrutas, pero `/ordersomething` no es ninguna de estas.
 */
const JOPIT_OWNED_PATHS = ["/user/profile", "/orders", "/reviews", "/tryon", "/fittingroom", "/studio"];

/*
 * Las rutas de sesión (`/login`, `/register/*`, `/recover`, `/reset`, `/email/verify`, ...) NO
 * están en esa lista, y es a propósito. Son la maquinaria del propio handoff: el comprador sale al
 * host canónico por `loginLinkOf` y vuelve acá con `?jopit_code=`. Mandarlas de paseo agrega un
 * salto en el único flujo que ya cruza de host solo, y si alguna vez un `return_to` apuntara a
 * `/login` el redirect se volvería un rulo. Nadie linkea a esas rutas desde un tenant.
 */

export function isJopitOwnedPath(pathname) {
    return JOPIT_OWNED_PATHS.some(
        (owned) => pathname === owned || pathname.startsWith(`${owned}/`),
    );
}

/**
 * El href al que hay que mandar a alguien que cayó en una ruta de Jopit dentro de un tenant, o
 * `null` si no hay que mandarlo a ningún lado.
 *
 * `linkOf` es `marketplaceLinkOf` del toolkit, inyectado para poder probar esto sin tocar
 * `window`. Lleva `withReturnToShop` para que la pantalla de destino pueda ofrecer "volver a la
 * tienda": el comprador está de paso, no se está yendo.
 *
 * Devuelve `null` cuando el link no resulta externo — o sea, cuando ya estamos en el host
 * canónico. Sin esa guarda, un cambio futuro que dejara `marketplaceLinkOf` devolviendo un path
 * relativo haría que la página se redirija a sí misma para siempre.
 */
export function marketplaceRedirectHrefFor(linkOf, location) {
    if (!location || !isJopitOwnedPath(location.pathname)) return null;

    const { href, isExternal } = linkOf(`${location.pathname}${location.search || ""}`, {
        withReturnToShop: true,
    });

    return isExternal ? href : null;
}
